const ROME_TZ = "Europe/Rome";

const romeTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: ROME_TZ,
  hour: "numeric",
  minute: "numeric",
  hour12: false,
});

const parseHourToken = (token) => {
  const value = Number(String(token).trim());
  if (!Number.isInteger(value) || value < 0 || value > 23) {
    return null;
  }
  return value;
};

const parseMinuteToken = (token) => {
  const value = Number(String(token).trim());
  if (!Number.isInteger(value) || value < 0 || value > 59) {
    return null;
  }
  return value;
};

export const parseTimeToken = (rawValue) => {
  const match = String(rawValue || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = parseHourToken(match[1]);
  const minute = parseMinuteToken(match[2]);
  if (hour == null || minute == null) {
    return null;
  }
  return { hour, minute };
};

export const formatTimeToken = (hour, minute) => (
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
);

export const formatRomeTimeLabel = (hour, minute) => formatTimeToken(hour, minute);

export const isValidRomeTime = (hour, minute) => (
  parseHourToken(hour) != null && parseMinuteToken(minute) != null
);

export const normalizeRomeTimeList = (rawValue) => {
  const items = Array.isArray(rawValue) ? rawValue : [];
  const seen = new Set();
  const normalized = [];

  items.forEach((item) => {
    let hour;
    let minute;
    if (typeof item === "object" && item != null) {
      hour = parseHourToken(item.hour);
      minute = parseMinuteToken(item.minute);
    } else {
      const parsed = parseTimeToken(item);
      if (!parsed) {
        return;
      }
      ({ hour, minute } = parsed);
    }
    if (hour == null || minute == null) {
      return;
    }
    const key = formatTimeToken(hour, minute);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push({ hour, minute });
  });

  return normalized.sort(
    (left, right) => (left.hour * 60 + left.minute) - (right.hour * 60 + right.minute),
  );
};

export const normalizeUtcTimeList = (rawValue) => {
  const items = Array.isArray(rawValue) ? rawValue : [];
  const seen = new Set();
  const normalized = [];
  items.forEach((item) => {
    const parsed = parseTimeToken(item);
    if (!parsed) {
      return;
    }
    const key = formatTimeToken(parsed.hour, parsed.minute);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(key);
  });
  return normalized.sort();
};

const readRomeTimeParts = (instant) => {
  const parts = romeTimeFormatter.formatToParts(instant);
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? -1);
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? -1);
  return {
    hour: hour === 24 ? 0 : hour,
    minute,
  };
};

export const romeTimeToUtcTime = (romeHour, romeMinute, referenceDate = new Date()) => {
  const year = referenceDate.getUTCFullYear();
  const month = referenceDate.getUTCMonth();
  const day = referenceDate.getUTCDate();

  for (let utcHour = 0; utcHour < 24; utcHour += 1) {
    for (let utcMinute = 0; utcMinute < 60; utcMinute += 1) {
      const instant = new Date(Date.UTC(year, month, day, utcHour, utcMinute, 0, 0));
      const rome = readRomeTimeParts(instant);
      if (rome.hour === romeHour && rome.minute === romeMinute) {
        return { hour: utcHour, minute: utcMinute };
      }
    }
  }
  return { hour: romeHour, minute: romeMinute };
};

export const utcTimeToRomeTime = (utcHour, utcMinute, referenceDate = new Date()) => {
  const year = referenceDate.getUTCFullYear();
  const month = referenceDate.getUTCMonth();
  const day = referenceDate.getUTCDate();
  const instant = new Date(Date.UTC(year, month, day, utcHour, utcMinute, 0, 0));
  return readRomeTimeParts(instant);
};

export const romeTimesToUtcTimes = (romeTimes, referenceDate = new Date()) => {
  const normalized = normalizeRomeTimeList(romeTimes);
  const utcTokens = normalized.map(({ hour, minute }) => {
    const utc = romeTimeToUtcTime(hour, minute, referenceDate);
    return formatTimeToken(utc.hour, utc.minute);
  });
  return normalizeUtcTimeList(utcTokens);
};

export const utcTimesToRomeTimes = (utcTimes, referenceDate = new Date()) => {
  const normalized = normalizeUtcTimeList(utcTimes);
  return normalized.map((token) => {
    const parsed = parseTimeToken(token);
    if (!parsed) {
      return null;
    }
    return utcTimeToRomeTime(parsed.hour, parsed.minute, referenceDate);
  }).filter(Boolean);
};

const migrateLegacyUtcHours = (utcHours) => (
  Array.isArray(utcHours)
    ? utcHours.map((hour) => formatTimeToken(Number(hour), 0))
    : []
);

export const detectScheduleMode = (config = {}) => {
  const utcTimes = normalizeUtcTimeList(config.scheduled_import_times_utc);
  if (utcTimes.length > 0) {
    return "times";
  }
  const legacyHours = migrateLegacyUtcHours(config.scheduled_import_hours_utc);
  if (legacyHours.length > 0) {
    return "times";
  }
  return "off";
};

export const SCHEDULE_MODE_OPTIONS = [
  { value: "off", label: "Disattivato" },
  { value: "times", label: "Orari fissi (Europe/Rome)" },
];

export const LOG_LEVEL_OPTIONS = [
  { value: "DEBUG", label: "DEBUG" },
  { value: "INFO", label: "INFO" },
  { value: "WARNING", label: "WARNING" },
  { value: "ERROR", label: "ERROR" },
  { value: "CRITICAL", label: "CRITICAL" },
];

export const parseEmailList = (rawValue) => (
  String(rawValue || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
);

export const formatEmailList = (emails) => (
  Array.isArray(emails) ? emails.join(", ") : ""
);

const EMAIL_RECIPIENT_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidReportRecipientEmail = (value) => (
  EMAIL_RECIPIENT_PATTERN.test(String(value || "").trim())
);

export const normalizeEmailList = (rawValue) => {
  const items = Array.isArray(rawValue) ? rawValue : parseEmailList(rawValue);
  const seen = new Set();
  const normalized = [];
  items.forEach((item) => {
    const trimmed = String(item || "").trim();
    if (!trimmed) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(trimmed);
  });
  return normalized;
};

export const configToDraft = (config = {}) => {
  const utcTimes = normalizeUtcTimeList(config.scheduled_import_times_utc);
  const legacyUtcTimes = migrateLegacyUtcHours(config.scheduled_import_hours_utc);
  const effectiveUtcTimes = utcTimes.length > 0 ? utcTimes : legacyUtcTimes;
  const scheduleMode = detectScheduleMode(config);
  const logLevel = String(config.log_level || "INFO").trim().toUpperCase();

  return {
    importEnabled: config.import_blocked === false,
    scheduleMode,
    scheduledTimesRomeList: utcTimesToRomeTimes(effectiveUtcTimes),
    importVendorBatchSize: config.import_vendor_batch_size ?? 3,
    importVendorPauseSeconds: config.import_vendor_pause_seconds ?? 30,
    importBatchSize: config.import_batch_size ?? 1000,
    importBulkExecuteChunkSize: config.import_bulk_execute_chunk_size ?? 1000,
    importSeenTempTableThreshold: config.import_seen_temp_table_threshold ?? 500,
    reindexFullAfterScheduledImport: Boolean(config.reindex_full_after_scheduled_import),
    reindexFullAfterManualAllImport: Boolean(config.reindex_full_after_manual_all_import),
    reindexVendorAfterManualSelectedImport: Boolean(config.reindex_vendor_after_manual_selected_import),
    reindexVendorAfterManualSingleImport: Boolean(config.reindex_vendor_after_manual_single_import),
    reindexVendorAfterManualStoredImport: Boolean(config.reindex_vendor_after_manual_stored_import),
    sendEmailAfterScheduledImport: Boolean(config.send_email_after_scheduled_import),
    sendEmailAfterManualAllImport: Boolean(config.send_email_after_manual_all_import),
    sendEmailAfterManualSelectedImport: Boolean(config.send_email_after_manual_selected_import),
    sendEmailAfterManualSingleImport: Boolean(config.send_email_after_manual_single_import),
    sendEmailAfterManualStoredImport: Boolean(config.send_email_after_manual_stored_import),
    internalRicercaBaseUrl: config.internal_ricerca_base_url || config.internal_api_base_url || "",
    httpTriggerTimeoutSeconds: config.http_trigger_timeout_seconds ?? 20,
    persistImportJobs: config.persist_import_jobs !== false,
    importReportRecipients: normalizeEmailList(config.import_report_recipients),
    tmpDir: config.tmp_dir || "_tmp",
    logLevel: LOG_LEVEL_OPTIONS.some((item) => item.value === logLevel) ? logLevel : "INFO",
  };
};

export const draftToPayload = (draft) => {
  const payload = {
    import_blocked: !draft.importEnabled,
    import_vendor_batch_size: Number(draft.importVendorBatchSize),
    import_vendor_pause_seconds: Number(draft.importVendorPauseSeconds),
    import_batch_size: Number(draft.importBatchSize),
    import_bulk_execute_chunk_size: Number(draft.importBulkExecuteChunkSize),
    import_seen_temp_table_threshold: Number(draft.importSeenTempTableThreshold),
    reindex_full_after_scheduled_import: Boolean(draft.reindexFullAfterScheduledImport),
    reindex_full_after_manual_all_import: Boolean(draft.reindexFullAfterManualAllImport),
    reindex_vendor_after_manual_selected_import: Boolean(draft.reindexVendorAfterManualSelectedImport),
    reindex_vendor_after_manual_single_import: Boolean(draft.reindexVendorAfterManualSingleImport),
    reindex_vendor_after_manual_stored_import: Boolean(draft.reindexVendorAfterManualStoredImport),
    send_email_after_scheduled_import: Boolean(draft.sendEmailAfterScheduledImport),
    send_email_after_manual_all_import: Boolean(draft.sendEmailAfterManualAllImport),
    send_email_after_manual_selected_import: Boolean(draft.sendEmailAfterManualSelectedImport),
    send_email_after_manual_single_import: Boolean(draft.sendEmailAfterManualSingleImport),
    send_email_after_manual_stored_import: Boolean(draft.sendEmailAfterManualStoredImport),
    internal_ricerca_base_url: String(draft.internalRicercaBaseUrl || "").trim(),
    http_trigger_timeout_seconds: Number(draft.httpTriggerTimeoutSeconds),
    persist_import_jobs: Boolean(draft.persistImportJobs),
    import_report_recipients: normalizeEmailList(draft.importReportRecipients),
    tmp_dir: String(draft.tmpDir || "_tmp").trim() || "_tmp",
    log_level: String(draft.logLevel || "INFO").trim().toUpperCase(),
  };

  if (draft.scheduleMode === "times") {
    payload.scheduled_import_times_utc = romeTimesToUtcTimes(draft.scheduledTimesRomeList);
  } else {
    payload.scheduled_import_times_utc = [];
  }

  return payload;
};
